import React, { useState, useContext, createContext, useCallback, FC, ReactNode, useEffect } from 'react';

interface FeedbackTrackerContextType {
  aiResponseCount: number;
  hasFeedbackBeenSubmitted: boolean;
  incrementAIResponseCount: () => void;
  markFeedbackAsSubmitted: () => void;
  shouldOpenFeedbackForm: boolean;
  setShouldOpenFeedbackForm: (value: boolean) => void;
}

const FeedbackTrackerContext = createContext<FeedbackTrackerContextType | undefined>(undefined);

interface FeedbackTrackerProviderProps {
  children: ReactNode;
  responseThreshold?: number;
}

export const FeedbackTrackerProvider: FC<FeedbackTrackerProviderProps> = ({ 
  children, 
  responseThreshold = 3 
}) => {
  const [aiResponseCount, setAiResponseCount] = useState<number>(0);
  const [hasFeedbackBeenSubmitted, setHasFeedbackBeenSubmitted] = useState<boolean>(false);
  const [shouldOpenFeedbackForm, setShouldOpenFeedbackForm] = useState<boolean>(false);

  // Increment the AI response count
  const incrementAIResponseCount = useCallback(() => {
    setAiResponseCount(prev => prev + 1);
  }, []);

  // Mark feedback as submitted
  const markFeedbackAsSubmitted = useCallback(() => {
    setHasFeedbackBeenSubmitted(true);
  }, []);

  // Auto-open feedback form after threshold is reached
  useEffect(() => {
    if (aiResponseCount === responseThreshold && !hasFeedbackBeenSubmitted) {
      const timer = setTimeout(() => {
        setShouldOpenFeedbackForm(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [aiResponseCount, hasFeedbackBeenSubmitted, responseThreshold]);

  const value = {
    aiResponseCount,
    hasFeedbackBeenSubmitted,
    incrementAIResponseCount,
    markFeedbackAsSubmitted,
    shouldOpenFeedbackForm,
    setShouldOpenFeedbackForm,
  };

  return (
    <FeedbackTrackerContext.Provider value={value}>
      {children}
    </FeedbackTrackerContext.Provider>
  );
};

export const useFeedbackTracker = (): FeedbackTrackerContextType => {
  const context = useContext(FeedbackTrackerContext);
  if (context === undefined) {
    throw new Error('useFeedbackTracker must be used within a FeedbackTrackerProvider');
  }
  return context;
};